/**
 * The words the family sees for an account. The database stores `credit_card` and
 * `monthly_summary`; nobody in the house says that (skill 13 — never show internal terms).
 */
import {
  isLiabilityAccountType,
  type AccountType,
  type CaptureMode,
  type UsageScope,
} from '@pfo/domain';

import { formatVnd } from '../../../shared/lib/money.js';

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  cash: 'Tiền mặt',
  bank: 'Ngân hàng',
  e_wallet: 'Ví điện tử',
  broker_cash: 'Tiền ở công ty chứng khoán',
  broker_investment: 'Chứng khoán đang nắm giữ',
  savings: 'Tiết kiệm',
  other_asset: 'Tài sản khác',
  liability: 'Khoản nợ',
  credit_card: 'Thẻ tín dụng',
};

export const USAGE_SCOPE_LABELS: Record<UsageScope, string> = {
  personal: 'Của riêng tôi',
  household: 'Của gia đình',
  mixed: 'Dùng chung',
};

export const CAPTURE_MODE_LABELS: Record<CaptureMode, string> = {
  manual: 'Nhập từng giao dịch',
  monthly_summary: 'Nhập tổng cuối tháng',
  statement_import: 'Nhập từ sao kê',
  api_sync: 'Tự động đồng bộ',
  mixed: 'Kết hợp',
};

/**
 * A liability's balance is negative in the ledger — that is the accounting truth, and it is
 * not what a person wants to read. `Math.abs` here flips the sign of ONE exact integer for
 * display; it is not aggregation, which stays in SQL (DBD-014).
 */
export function formatAccountBalance(balance: number, accountType: AccountType): string {
  if (!isLiabilityAccountType(accountType)) {
    return formatVnd(balance);
  }

  if (balance === 0) return 'Đã trả hết';
  // A liability in credit means more was paid than owed — worth saying plainly instead of
  // printing "Đang nợ -500.000".
  if (balance > 0) return `Trả dư ${formatVnd(balance)}`;
  return `Đang nợ ${formatVnd(Math.abs(balance))}`;
}

/** Column wording for the monthly statement: a card is charged and paid, an account moves. */
export function statementLabels(accountType: AccountType): {
  opening: string;
  in: string;
  out: string;
  closing: string;
} {
  return isLiabilityAccountType(accountType)
    ? {
        opening: 'Dư nợ đầu kỳ',
        // On a liability the CREDIT side is what was added to the debt (a card swipe) and
        // the DEBIT side is what was paid off.
        in: 'Phát sinh thêm',
        out: 'Đã trả',
        closing: 'Dư nợ cuối kỳ',
      }
    : { opening: 'Đầu kỳ', in: 'Tiền vào', out: 'Tiền ra', closing: 'Cuối kỳ' };
}

const MONTH_FORMATTER = new Intl.DateTimeFormat('vi-VN', {
  month: 'long',
  year: 'numeric',
  // The value is a date-only period start, so it is read and written in UTC: formatting it
  // in the device timezone would label the month before it on any device west of Greenwich.
  timeZone: 'UTC',
});

/** "tháng 8 năm 2026" from the `period_start` date of a statement row. */
export function formatStatementMonth(periodStart: string): string {
  return MONTH_FORMATTER.format(new Date(`${periodStart}T00:00:00Z`));
}
